import React from "react";
import { Box, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import Add from "@mui/icons-material/Add";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import Grid2 from "@mui/material/Grid2";
import { ACCENT, LOGO_FIELD_LABEL, PRIMARY } from "../../constants";
import { profileReadOnlyTextFieldSx } from "../../profileFieldStyles";

const LOGO_FIELD_ID = "vendor-field-logo";
const LOGO_STATUS_LOADED = "Caricato";
const LOGO_STATUS_NOT_LOADED = "Non caricato";
const LOGO_UPLOAD_TOOLTIP = "Carica logo. PNG o JPEG, massimo 1 MB.";
const LOGO_ADD_TOOLTIP = "Aggiungi logo. PNG o JPEG, massimo 1 MB.";
const LOGO_REMOVE_TOOLTIP = "Rimuovi logo";
const LOGO_CHECK_COLOR = "#2e7d32";

const TOOLTIP_PROPS = { enterTouchDelay: 0, disableInteractive: true };

const logoFieldSx = {
  ...profileReadOnlyTextFieldSx,
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...profileReadOnlyTextFieldSx["& .MuiOutlinedInput-root"],
    color: PRIMARY,
    WebkitTextFillColor: PRIMARY,
    cursor: "default",
    minHeight: 52,
    minWidth: 150,
    alignItems: "center",
    py: 0,
  },
  "& .MuiInputBase-input": {
    color: PRIMARY,
    WebkitTextFillColor: PRIMARY,
    width: 0,
    minWidth: 0,
    flex: "0 0 0",
    p: 0,
    m: 0,
    opacity: 0,
    height: 0,
  },
  "& .MuiInputAdornment-root": {
    color: PRIMARY,
    WebkitTextFillColor: PRIMARY,
    maxHeight: "none",
    height: "auto",
  },
};

function LogoStatusAdornment({ showLogoPreview }) {
  return (
    <InputAdornment position="start" sx={{ mr: 0, maxHeight: "none", ml: "14px" }}>
      {showLogoPreview ? (
        <CheckCircleOutline
          aria-hidden
          sx={{ color: LOGO_CHECK_COLOR, fontSize: 20, mr: 0.75, flexShrink: 0 }}
        />
      ) : null}
      <Typography
        component="span"
        sx={{
          fontFamily: "Rubik, sans-serif",
          fontSize: "0.95rem",
          fontWeight: 600,
          fontStyle: "normal",
          color: "#000000",
          lineHeight: 1.5,
        }}
      >
        {showLogoPreview ? LOGO_STATUS_LOADED : LOGO_STATUS_NOT_LOADED}
      </Typography>
    </InputAdornment>
  );
}

function LogoActionAdornment({ showLogoPreview, onUpload, onRemove }) {
  if (showLogoPreview) {
    return (
      <InputAdornment position="end" sx={{ mr: 0.5, gap: 0.25 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={LOGO_UPLOAD_TOOLTIP} arrow placement="top" {...TOOLTIP_PROPS}>
            <IconButton
              size="small"
              aria-label="Carica logo"
              onClick={onUpload}
              sx={{ p: 0.5, color: PRIMARY }}
            >
              <EditOutlined sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={LOGO_REMOVE_TOOLTIP} arrow placement="top" {...TOOLTIP_PROPS}>
            <IconButton
              size="small"
              aria-label="Rimuovi logo"
              onClick={onRemove}
              sx={{ p: 0.5, color: ACCENT }}
            >
              <DeleteOutline sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </InputAdornment>
    );
  }

  return (
    <InputAdornment position="end" sx={{ mr: 0.5 }}>
      <Tooltip title={LOGO_ADD_TOOLTIP} arrow placement="top" {...TOOLTIP_PROPS}>
        <IconButton
          size="small"
          aria-label="Aggiungi logo"
          onClick={onUpload}
          sx={{ p: 0.5, color: PRIMARY }}
        >
          <Add sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );
}

export function ProfileLogoField({
  readOnly = false,
  showLogoPreview = false,
  fileInputRef,
  onFileChange,
  onRemoveLogo,
  errorMessage,
}) {
  const openFilePicker = () => fileInputRef?.current?.click();

  const handleRemoveClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRemoveLogo?.();
  };

  return (
    <Grid2 xs={12} md={6}>
      {!readOnly ? (
        <input
          type="file"
          accept="image/png, image/jpeg"
          ref={fileInputRef}
          onChange={onFileChange}
          style={{ display: "none" }}
        />
      ) : null}
      <TextField
        id={LOGO_FIELD_ID}
        label={LOGO_FIELD_LABEL}
        value=" "
        fullWidth
        variant="outlined"
        size="medium"
        error={!!errorMessage}
        helperText={errorMessage || ""}
        sx={logoFieldSx}
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true, "aria-readonly": true, tabIndex: -1 }}
        InputProps={{
          readOnly: true,
          startAdornment: <LogoStatusAdornment showLogoPreview={showLogoPreview} />,
          endAdornment: readOnly ? null : (
            <LogoActionAdornment
              showLogoPreview={showLogoPreview}
              onUpload={openFilePicker}
              onRemove={handleRemoveClick}
            />
          ),
        }}
      />
    </Grid2>
  );
}
